import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { Application } from '@feathersjs/feathers';
import { AddDistrictTypeModal } from '../../components';
import { humanizeDistrictType } from '../../app/util/humanize-district-type';
import { StateConfiguration } from '../../app/feathers/state-configurations/StateConfiguration';
import { ServiceTypes } from '../../app/feathers/ServiceTypes';
import { CandidateList, PatchCandidateList, ScoutingConfigurationEntry } from '../../app/feathers/candidate-lists/CandidateList';
import { Election } from '../../app/feathers/elections/Election';

export interface ScoutingStepContentProps {
  candidateList: CandidateList;
  election: Election | null;
  electionKey: string;
  feathers: Application<ServiceTypes>;
  onConfirmScouting: () => Promise<void>;
  onPatchCandidateList: (data: PatchCandidateList) => Promise<unknown>;
}

export const ScoutingStepContent = ({
  candidateList,
  election,
  electionKey,
  feathers,
  onConfirmScouting,
  onPatchCandidateList
}: ScoutingStepContentProps) => {
  const [savingScoutingConfiguration, setSavingScoutingConfiguration] = useState(false);
  const [savingEstimatedMinutes, setSavingEstimatedMinutes] = useState(false);
  const [stateConfiguration, setStateConfiguration] = useState<StateConfiguration | null>(null);
  const [estimatedScoutingMinutesInput, setEstimatedScoutingMinutesInput] = useState(
    candidateList?.estimatedScoutingMinutes != null ? `${candidateList.estimatedScoutingMinutes}` : ''
  );
  const stateName = election?.stateName ?? '';

  useEffect(() => {
    if (!feathers || !election?.stateCode) return;
    feathers
      .getService('state-configurations')
      .get((election.stateCode || '').toLowerCase())
      .then((config: StateConfiguration) => setStateConfiguration(config))
      .catch(() => {});
  }, [feathers, election?.stateCode]);

  useEffect(() => {
    setEstimatedScoutingMinutesInput(
      candidateList?.estimatedScoutingMinutes != null ? `${candidateList.estimatedScoutingMinutes}` : ''
    );
  }, [candidateList?.estimatedScoutingMinutes]);

  const handleSaveScoutingConfiguration = async (newConfig: ScoutingConfigurationEntry[]) => {
    if (!candidateList?._id || !feathers) return;
    setSavingScoutingConfiguration(true);
    try {
      await onPatchCandidateList({ scoutingConfiguration: newConfig });
    } catch (err) {
      console.error('Error saving scouting configuration:', err);
    } finally {
      setSavingScoutingConfiguration(false);
    }
  };

  const scoutingConfig = candidateList.scoutingConfiguration || [];

  const handleSaveEstimatedScoutingMinutes = async () => {
    if (!candidateList?._id || !feathers) return;

    const rawValue = estimatedScoutingMinutesInput.trim();
    let estimatedScoutingMinutes: number | null = null;
    if (rawValue !== '') {
      const parsedMinutes = Number(rawValue);
      if (!Number.isFinite(parsedMinutes) || parsedMinutes < 0) {
        return;
      }
      estimatedScoutingMinutes = parsedMinutes;
    }

    setSavingEstimatedMinutes(true);
    try {
      await onPatchCandidateList({ estimatedScoutingMinutes });
    } catch (err) {
      console.error('Error saving estimated scouting minutes:', err);
    } finally {
      setSavingEstimatedMinutes(false);
    }
  };

  return (
    <>
      <Typography variant="body1" style={{ marginBottom: '16px', marginTop: '16px', color: '#666' }}>
        Determine which district types under the state should be marked as complete when creating this candidate list.
        The state is always marked complete.
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, marginBottom: '16px' }}>
        <InfoOutlinedIcon style={{ fontSize: 16, cursor: 'help' }} />
        <Typography variant="body2" color="textPrimary">
          Defaults can be loaded from the state configuration, which you can edit{' '}
          <Link
            to={`/state-election-configurations/${(stateName || '').replace(/\s+/g, '-').toLowerCase()}`}
            style={{ textDecoration: 'underline' }}
          >
            here
          </Link>
          .
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, marginBottom: 2 }}>
        {scoutingConfig.map((config, index) => (
          <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body1" style={{ minWidth: '120px', fontWeight: 500 }}>
              {humanizeDistrictType(config.districtType, stateConfiguration?.typeConfigurations ?? null)}
            </Typography>
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <Select
                value={config.coverageExtent}
                onChange={(e) => {
                  const newConfig = [...scoutingConfig];
                  newConfig[index] = {
                    ...newConfig[index],
                    coverageExtent: e.target.value as 'full-coverage' | 'limited-coverage' | 'no-coverage'
                  };
                  handleSaveScoutingConfiguration(newConfig);
                }}
                disabled={savingScoutingConfiguration}
                sx={{ backgroundColor: 'white', '& .MuiSelect-select': { padding: '8px 32px 8px 12px' } }}
              >
                <MenuItem value="full-coverage">Mark all as complete</MenuItem>
                <MenuItem value="limited-coverage">Mark only those in the content</MenuItem>
                <MenuItem value="no-coverage">Mark none</MenuItem>
              </Select>
            </FormControl>
          </Box>
        ))}
      </Box>
      {stateName && (
        <AddDistrictTypeModal
          stateName={stateName}
          existingTypes={scoutingConfig.map((c) => c.districtType)}
          excludeTypes={['state', ...scoutingConfig.map((c) => c.districtType)]}
          restrictToTiers={[2]}
          onSelect={(districtType) => {
            if (!scoutingConfig.some((c) => c.districtType === districtType)) {
              handleSaveScoutingConfiguration([
                ...scoutingConfig,
                { districtType, coverageExtent: 'limited-coverage' }
              ]);
            }
          }}
          buttonComponent={({ onClick }) => (
            <Button
              variant="outlined"
              size="small"
              onClick={onClick}
              disabled={savingScoutingConfiguration}
            >
              Add district type
            </Button>
          )}
        />
      )}
      <Divider style={{ margin: '24px 0 16px' }} />
      <Box sx={{}}>
        <Typography variant="body1" style={{ marginBottom: '16px', color: '#666' }}>
          Enter the total time, in minutes, for the entire upload/scouting process for this candidate list.
        </Typography>
        <TextField
          sx={{ maxWidth: 420 }}
          fullWidth
          size="small"
          type="number"
          label="Estimated scouting time (minutes)"
          value={estimatedScoutingMinutesInput}
          onChange={(event) => setEstimatedScoutingMinutesInput(event.target.value)}
          onBlur={handleSaveEstimatedScoutingMinutes}
          disabled={savingEstimatedMinutes}
          inputProps={{ min: 0, step: 1 }}
        />
      </Box>
      <Divider style={{ margin: '24px 0' }} />
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '24px' }}>
        <Button
          variant="contained"
          color="primary"
          size="large"
          onClick={onConfirmScouting}
          disabled={savingScoutingConfiguration || scoutingConfig.length === 0 || !estimatedScoutingMinutesInput}
          style={{ minWidth: '200px' }}
        >
          Confirm Scouting
        </Button>
      </div>
    </>
  );
};
